/**
 * CSV import (S05).
 *
 * The exact file format is not settled yet and is expected to change, so nothing
 * here assumes one. A file is read by matching its header row against known
 * aliases per field; a file with no recognisable header falls back to a fixed
 * positional order. Either way the mapping that was used comes back in the
 * response, so an operator can see how their columns were read rather than
 * guessing from the resulting contacts.
 *
 * The salutation column is optional in both paths — with a header it is simply
 * one more alias to match, and without one it is the fifth position if present.
 * Offer does not exist in this build at all, but an `offer` column in an
 * uploaded file is still ignored rather than rejected, so files exported from the
 * old tool keep importing. Tolerating a column we have no use for costs nothing;
 * refusing a file over it would cost somebody their afternoon.
 */
import { z } from 'zod';

import { emailSchema, phoneSchema } from './contacts.js';
import { Salutation } from './enums.js';

/** The fields an import can fill. */
export const CSV_FIELDS = ['name', 'company', 'phone', 'email', 'salutation'] as const;
export type CsvField = (typeof CSV_FIELDS)[number];

/**
 * Header aliases per field, compared after normalisation (lower-cased with
 * everything that is not a letter or digit removed, so `E-Mail`, `e mail` and
 * `email` are one alias).
 *
 * German and Bulgarian forms are included because the client is German and the
 * contact data is Bulgarian: a file exported from either side's spreadsheet
 * should import without being renamed first.
 */
export const CSV_HEADER_ALIASES: Record<CsvField, readonly string[]> = {
  name: ['name', 'fullname', 'contact', 'contactname', 'person', 'kontakt', 'име', 'имена'],
  company: [
    'company',
    'companyname',
    'organisation',
    'organization',
    'org',
    'account',
    'firm',
    'firma',
    'unternehmen',
    'компания',
    'фирма',
  ],
  phone: [
    'phone',
    'phonenumber',
    'mobile',
    'mobilephone',
    'telephone',
    'tel',
    'number',
    'msisdn',
    'telefon',
    'telefonnummer',
    'mobil',
    'телефон',
  ],
  email: ['email', 'emailaddress', 'mail', 'mailaddress', 'emailadresse', 'имейл', 'мейл'],
  salutation: ['salutation', 'greeting', 'title', 'anrede', 'obraschtenie', 'обръщение'],
};

/**
 * Column order assumed when the first row is not a recognisable header.
 *
 * Chosen to match the documented format rather than the old tool's, which had an
 * `offer` column in fourth position. A headerless file from the old tool will
 * therefore read its offer column as an email and reject those rows — visibly,
 * in the report, naming the mapping that was assumed. That is the honest failure
 * mode: the alternative is guessing, and a guess that lands wrong writes bad
 * phone numbers into the calling list.
 */
export const CSV_POSITIONAL_ORDER = [
  'name',
  'company',
  'phone',
  'email',
  'salutation',
] as const satisfies readonly CsvField[];

/**
 * Salutation spellings accepted in the file, normalised the same way as headers.
 *
 * An unrecognised value is not an error: the row imports with no salutation and
 * the value is reported, because refusing a contact over how somebody wrote
 * "Herr" would be a poor trade.
 */
export const CSV_SALUTATION_ALIASES: Record<string, Salutation> = {
  mr: 'MR',
  mister: 'MR',
  herr: 'MR',
  hr: 'MR',
  sir: 'MR',
  господин: 'MR',
  гн: 'MR',
  ms: 'MS',
  miss: 'MS',
  frau: 'MS',
  fr: 'MS',
  госпожа: 'MS',
  гжа: 'MS',
  mrs: 'MRS',
  mx: 'MX',
  dr: 'DR',
  doktor: 'DR',
  doctor: 'DR',
  доктор: 'DR',
  prof: 'PROF',
  professor: 'PROF',
  professeur: 'PROF',
  проф: 'PROF',
  none: 'NONE',
  na: 'NONE',
};

/**
 * One accepted row, after normalisation and before it is written.
 *
 * `phone` is already E.164 here: normalising is the importer's job (it needs a
 * default region, which is server configuration), and validating a raw `0888 123
 * 456` against `phoneSchema` would reject a perfectly good number for its
 * formatting.
 */
export const csvContactRowSchema = z.object({
  name: z.string().trim().min(1),
  company: z.string().trim().min(1).nullable(),
  phone: phoneSchema,
  email: emailSchema.nullable(),
  salutation: Salutation.schema,
});
export type CsvContactRow = z.infer<typeof csvContactRowSchema>;

/** Whether the columns were read from a header row or assumed by position. */
export const CSV_MAPPING_SOURCE = ['header', 'positional'] as const;
export const csvMappingSourceSchema = z.enum(CSV_MAPPING_SOURCE);
export type CsvMappingSource = z.infer<typeof csvMappingSourceSchema>;

const columnIndex = z.number().int().nonnegative().nullable();

/**
 * How the file was read.
 *
 * Returned with every import so the mapping is visible rather than implied. A
 * `null` column is one the file did not carry, which for `salutation` and
 * `email` is normal and for `name` or `phone` is why every row was rejected.
 */
export const importMappingSchema = z.object({
  source: csvMappingSourceSchema,
  columns: z.object({
    name: columnIndex,
    company: columnIndex,
    phone: columnIndex,
    email: columnIndex,
    salutation: columnIndex,
  }),
  /** Header cells that matched no field, named so a typo is visible. */
  ignored: z.array(z.string()),
});
export type ImportMapping = z.infer<typeof importMappingSchema>;

/**
 * A row that was not imported, and why.
 *
 * `row` is the line number in the uploaded file, header included, so it matches
 * what a spreadsheet shows. `name` and `phone` are whatever the row held, even
 * when invalid — a report that says "row 74" without saying who is a report the
 * operator has to go and look up.
 */
export const importRejectionSchema = z.object({
  row: z.number().int().positive(),
  reason: z.string(),
  name: z.string().nullable(),
  phone: z.string().nullable(),
});
export type ImportRejection = z.infer<typeof importRejectionSchema>;

/** Values that were adjusted rather than rejected — reported, never silent. */
export const importWarningSchema = z.object({
  row: z.number().int().positive(),
  message: z.string(),
});
export type ImportWarning = z.infer<typeof importWarningSchema>;

export const importResultSchema = z.object({
  importId: z.string().uuid(),
  filename: z.string(),
  /** Data rows read, excluding a header row if there was one. */
  rows: z.number().int().nonnegative(),
  created: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  mapping: importMappingSchema,
  rejected: z.array(importRejectionSchema),
  warnings: z.array(importWarningSchema),
});
export type ImportResult = z.infer<typeof importResultSchema>;
